import { useQuery } from "@tanstack/react-query";
import {
  getPageDataErrorCode,
  isPriceReferenceContextInvalidError,
  isSessionDuplicateInvariantError,
  sessionQueryOptions,
} from "~/app/api/pageData";
import type { AnalyticsData, CommerceContextData, SessionData, UserData } from "~/app/store/bffApi";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";

const selectUser = (data: SessionData) => data.user;
const selectAnalytics = (data: SessionData) => data.analytics;
const selectCommerceContext = (data: SessionData) => data.commerceContext;

export function useSessionSelector<T>(select: (data: SessionData) => T): T | null {
  const query = useQuery({
    ...sessionQueryOptions(),
    select,
  });
  return isSessionDuplicateInvariantError(query.error) ? null : query.data ?? null;
}

/**
 * Compatibility hook for existing consumers. Prefer focused selector hooks for new code.
 */
export function useSession(): SessionData | null {
  const query = useQuery(sessionQueryOptions());
  return isSessionDuplicateInvariantError(query.error) ? null : query.data ?? null;
}

export type PriceReferenceSelectionStatus = "loading" | "ready" | "error";

export interface PriceReferenceSelectionState {
  selection?: PriceReferenceSelection;
  status: PriceReferenceSelectionStatus;
  errorCode?: string;
  isFetching: boolean;
}

/** Session-backed selection state. Cached data remains available while a refetch is in flight. */
export function usePriceReferenceSelection(): PriceReferenceSelectionState {
  const query = useQuery(sessionQueryOptions());
  const duplicateInvariant = isSessionDuplicateInvariantError(query.error);
  const selection = duplicateInvariant ? undefined : query.data?.commerceContext.priceReferenceSelection;
  const initialRequest = query.isPending && !query.data;
  const hasBlockingError =
    duplicateInvariant || isPriceReferenceContextInvalidError(query.error) || (query.isError && !query.data);

  return {
    selection,
    status: initialRequest ? "loading" : hasBlockingError ? "error" : "ready",
    errorCode: query.error ? getPageDataErrorCode(query.error) : undefined,
    isFetching: query.isFetching,
  };
}

export function useSessionUser(): UserData | null {
  return useSessionSelector(selectUser);
}

export function useSessionAnalytics(): AnalyticsData | null {
  return useSessionSelector(selectAnalytics);
}

export function useCommerceContext(): CommerceContextData | null {
  return useSessionSelector(selectCommerceContext);
}
